import React, { CSSProperties, useEffect, useState } from 'react';
import { FieldValidator, useField } from 'formik';
import { Label, Form } from 'semantic';
import { DropdownItemProps } from 'semantic-ui-react';

interface Props {
  name: string;
  label?: string;
  validate?: FieldValidator;
  options: DropdownItemProps[];
  clearable?: boolean;
  objectMode?: boolean;
  search?: boolean;
  required?: boolean;
  multiple?: boolean;
  selection?: boolean;
  fluid?: boolean;
  hideErrorLabel?: boolean;
  wrapperStyle?: CSSProperties;
  disabled?: boolean;
  hideInternalComponentError?: boolean;
  postChange?: (value: string | string[]) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  allowAdditions?: boolean;
  className?: string;
  onMount?: (value: string) => void;
}

const DropdownField: React.FC<Props> = ({
  name,
  label,
  validate,
  options,
  search = false,
  multiple = false,
  selection = false,
  objectMode = false,
  clearable = true,
  required = false,
  hideErrorLabel = false,
  hideInternalComponentError = false,
  fluid = false,
  wrapperStyle,
  disabled = false,
  postChange,
  placeholder,
  style,
  allowAdditions = false,
  className,
  onMount,
}) => {
  const [field, meta, helpers] = useField({ name, validate });
  const [dropdownOptions, setDropdownOptions] = useState(options);

  useEffect(() => {
    if (onMount) {
      onMount(field.value as string);
    }
  }, []);

  // Keep dropdownOptions in sync with props.options when allowAdditions is false
  useEffect(() => {
    if (!allowAdditions) {
      setDropdownOptions(options);
    }
  }, [options, allowAdditions]);

  let dropdownValue = field.value;
  if (multiple && !dropdownValue) {
    dropdownValue = [];
  }

  return (
    <Form.Field>
      <div style={wrapperStyle}>
        <Form.Dropdown
          className={className}
          search={search}
          clearable={clearable}
          allowAdditions={allowAdditions}
          fluid={fluid}
          value={dropdownValue}
          objectMode={objectMode}
          label={label}
          style={style}
          required={required}
          multiple={multiple}
          selection={selection}
          options={dropdownOptions}
          disabled={disabled}
          placeholder={placeholder}
          onAddItem={(e, { value }) => {
            const addedValue = value as string | number | boolean;
            const newOption: DropdownItemProps = {
              key: addedValue,
              text: String(addedValue),
              value: addedValue,
            };
            setDropdownOptions((prev) => [...prev, newOption]);
          }}
          onChange={(e, { value }) => {
            helpers.setValue(value, true);
            if (postChange) {
              postChange(value as string | string[]);
            }
          }}
          error={
            meta.touched && meta.error
              ? hideErrorLabel || hideInternalComponentError
                ? undefined
                : meta.error
              : undefined
          }
        />

        {/* We had to add this label to show error message box below the dropdown list */}
        {/* TODO: is meta.touched here required? this prevents Yup from showing errors on validate before submitting */}
        {meta.error && meta.touched && !hideErrorLabel && (
          <Label
            basic
            pointing="above"
            style={{
              color: '#9F3A38',
              marginTop: -15,
              border: '1px solid #E0B4B4',
            }}>
            {meta.error}
          </Label>
        )}
      </div>
    </Form.Field>
  );
};

export default DropdownField;
